import React, { useState } from 'react';
import { TrainingContent } from './TrainingContent';
import styled from 'styled-components';
import { PAGE_TEXT } from '../lang';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import { useMoveApplicationItem } from '../../../graphql/ApplicationForm/removeApplicationItem';
import { v4 as uuidv4 } from 'uuid';
import {
  Alert,
  type AlertDetails,
  Accordion,
  IconButton,
  type SelectOption,
} from '../../../../../components';

type TrainingPageProps = {
  applicationNo: string;
  trainingIds?: string[];
  enableEdit: boolean;
  enableDelete: boolean;
  countryList: SelectOption[];
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
  refetchApplication: () => void;
};

export const TrainingPage: React.FunctionComponent<
  TrainingPageProps
> = props => {
  // trainingId is the actual Id from backend
  const {
    applicationNo = '',
    trainingIds = [],
    enableEdit,
    countryList,
    enableDelete,
    refetchApplication,
    handleLoading,
  } = props;
  const { applicationId } = useParams();
  // keyId is the unique ID for frontend render
  const [keyIds, setKeyIds] = useState<string[]>(
    trainingIds.map(() => uuidv4()),
  );
  const removeApplicationItem = useMoveApplicationItem(refetchApplication);
  const { TRAINING } = PAGE_TEXT.OTHER_INFORMATION;

  const [openAlert, setOpenAlert] = React.useState(false);
  const [alertDetails, setAlertDetails] = React.useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });
  function addTrainForm(): void {
    setKeyIds([...keyIds, uuidv4()]);
  }

  const handleDelete =
    (index: number): (() => Promise<void>) =>
    async () => {
      if (index >= trainingIds.length) {
        const newKeyIds = [...keyIds];
        newKeyIds.splice(index, 1);
        setKeyIds(() => newKeyIds);
        setOpenAlert(true);
        setAlertDetails({
          alertContent: 'Delete Training/Internship Successfully.',
          alertType: 'success',
        });
        return;
      }

      await handleLoading(async () => {
        try {
          const id = trainingIds[index];
          const variables = {
            id: applicationId,
            input: {
              trainingId: id,
            },
          };
          await removeApplicationItem({ variables });
          const newKeyIds = [...keyIds];
          newKeyIds.splice(index, 1);
          setKeyIds(() => newKeyIds);
          setAlertDetails({
            alertContent: 'Deleted Training/Internship Successfully.',
            alertType: 'success',
          });
        } catch (e) {
          setAlertDetails({
            alertContent:
              'Deleted Training/Internship Failed.' + (e as Error).message,
            alertType: 'error',
          });
        }
      });
      setOpenAlert(true);
    };

  return (
    <Container>
      {keyIds.map((keyId, index: number) => {
        const details = (
          <TrainingContent
            applicationNo={applicationNo}
            key={keyId}
            index={index}
            enableEdit={enableEdit}
            enableDelete={enableDelete}
            countryList={countryList}
            trainingId={index >= trainingIds.length ? '' : trainingIds[index]}
            handleDelete={handleDelete(index)}
            refetchApplication={refetchApplication}
            handleLoading={handleLoading}
          />
        );
        return (
          <Accordion
            style={{ width: '100%' }}
            key={keyId}
            title={`${TRAINING.TITLE} - ${index + 1}`}
            defaultExpanded
            details={details}
          />
        );
      })}

      {keyIds.length < 10 && enableEdit && (
        <div className="add-button">
          <IconButton icon={<AddIcon />} onClick={addTrainForm} />
          {TRAINING.ADD_MORE_FORM}
        </div>
      )}
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;

  .MuiGrid-item {
    width: 100%;
  }

  h1 {
    margin-top: 2rem;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  .add-button {
    margin-top: 1rem;
    margin-bottom: 2rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;

    .MuiSvgIcon-root {
      background-color: #951d1d;
      color: #fff;
      border-radius: 50%;
      cursor: pointer;
    }

    button {
      margin-right: 1rem;
    }
  }
`;
